<script lang="ts">
  import { createQuery, useQueryClient } from '@tanstack/svelte-query';
  import { MapPin } from 'lucide-svelte';
  import { sitesApi } from '$lib/api/crm';
  import { session } from '$lib/state/session.svelte';
  import { route } from '$lib/state/route.svelte';
  import { getErrorMessage } from '$lib/api/client';
  import SearchBar from '$lib/components/ui/SearchBar.svelte';
  import SiteCards from './SiteCards.svelte';
  import SiteDashboard from './SiteDashboard.svelte';
  import CompanyFormModal from './CompanyFormModal.svelte';
  import type { SitePhase } from '$lib/types';
  import { ordersEnabled } from '$lib/utils/orders';

  const PHASE_CHIPS: Array<{ value: SitePhase | ''; label: string }> = [
    { value: '', label: 'All' },
    { value: 'contacting', label: 'Contacting' },
    { value: 'mockup', label: 'Mockup' },
    { value: 'material_supplied', label: 'Material supplied' },
    { value: 'installed', label: 'Installed' },
    { value: 'maintaining', label: 'Maintaining' },
    { value: 'closed', label: 'Closed' }
  ];

  const queryClient = useQueryClient();

  let search = $state('');
  let phaseFilter = $state<SitePhase | ''>('');
  let showCreate = $state(false);

  // Selected site lives in the URL hash (#/sites/<id>) and renders as its own
  // full page (SiteDashboard), same convention as Companies/Issues detail
  // views.
  const selectedSiteId = $derived(route.current.tab === 'sites' ? (route.current.id ?? null) : null);

  const verticalId = $derived(typeof session.selectedVertical === 'number' ? session.selectedVertical : undefined);
  const verticalSlug = $derived(session.me?.verticals.find((v) => v.id === verticalId)?.slug);
  // Sites only exist for a single selected Hotels or Lighting vertical — same
  // gating rule the Header's nav item uses to decide whether to show the tab
  // at all; this covers a direct #/sites link or a vertical switch while
  // already on this page.
  const sitesAvailable = $derived(verticalSlug === 'hotels' || verticalSlug === 'lighting' || ordersEnabled(session.me?.verticals.find(v => v.id === verticalId)));
  const isHotels = $derived(verticalSlug === 'hotels');

  const listQuery = createQuery(() => ({
    queryKey: ['sites', { verticalId, search }],
    queryFn: () => sitesApi.list({ vertical_id: verticalId as number, q: search || undefined }),
    enabled: sitesAvailable && verticalId !== undefined
  }));

  const filteredResults = $derived.by(() => {
    const rows = listQuery.data?.results ?? [];
    if (!phaseFilter) return rows;
    return rows.filter((r) => r.site_phase === phaseFilter);
  });

  function openDetail(id: number) {
    route.push({ tab: 'sites', id });
  }
</script>

{#if selectedSiteId != null && verticalId !== undefined}
  <SiteDashboard
    id={selectedSiteId}
    {isHotels}
    {verticalId}
    onClose={() => route.push({ tab: 'sites' })}
    onInvalid={() => route.replace({ tab: 'sites' })}
  />
{:else}
  <div class="tym-page">
    {#if !sitesAvailable}
      <div class="tym-card flex flex-col items-center justify-center px-6 py-12 text-center">
        <span class="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl" style="background:#E3F0F1; color:#226A73">
          <MapPin class="h-6 w-6" />
        </span>
        <div class="font-heading text-base font-bold text-navy">Select a Hotels, Lighting or order-enabled vertical</div>
        <div class="mt-1.5 max-w-[360px] text-[13px] text-dark-gray">
          Sites is an operational view for a single vertical — pick an order-enabled team, Hotels or Lighting from the switcher above.
        </div>
      </div>
    {:else}
      <div class="mb-4 flex flex-wrap items-center gap-2">
        {#each PHASE_CHIPS as chip (chip.value)}
          <button type="button" class="tym-chip {phaseFilter === chip.value ? 'active' : ''}" onclick={() => (phaseFilter = chip.value)}>
            {chip.label}
          </button>
        {/each}
      </div>

      <!-- "New site" only makes sense for Hotels: a Lighting site is a Deal,
           not a company, so creating one goes through the Deals form, not
           this modal (see the Hotels nav restructure spec, requirement 4). -->
      <SearchBar
        placeholder={isHotels ? 'Search sites…' : 'Search site name or order number…'}
        bind:value={search}
        onCreate={isHotels ? () => (showCreate = true) : undefined}
        createLabel="New site"
      />

      {#if listQuery.isLoading}
        <p class="tym-loading-state">Loading sites&hellip;</p>
      {:else if listQuery.isError}
        <p class="tym-error-state">{getErrorMessage(listQuery.error)}</p>
      {:else}
        <SiteCards rows={filteredResults} {isHotels} onOpen={openDetail} />
      {/if}
    {/if}
  </div>
{/if}

{#if showCreate}
  <CompanyFormModal
    defaultKind="hotel"
    lockKind={true}
    defaultVerticalId={verticalId}
    lockVertical={true}
    onClose={() => (showCreate = false)}
    onSaved={() => queryClient.invalidateQueries({ queryKey: ['sites'] })}
  />
{/if}
